import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ContactPicker from '../common/ContactPicker';
import { X, User, Mail, Phone, Briefcase, DollarSign, Link2, Copy, CheckCircle, Loader2, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function QuickIntakeModal({ isOpen, onClose, onSuccess, preFill = null }) {
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState([]);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdIntake, setCreatedIntake] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [emailSentTo, setEmailSentTo] = useState(null);
  const [emailFailure, setEmailFailure] = useState(null);

  const [linkedContact, setLinkedContact] = useState(null); // returning client
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    primary_phone: '',
    practice_area: '',
    source_id: '',
    consult_fee: 15000, // $150 default in cents
    consult_fee_waived: false
  });

  useEffect(() => {
    if (isOpen) {
      fetchMetadata();
      // Reset form when modal opens, with optional pre-fill data
      setFormData({
        first_name: preFill?.first_name || '',
        last_name: preFill?.last_name || '',
        email: preFill?.email || '',
        primary_phone: preFill?.primary_phone || '',
        practice_area: preFill?.practice_area || '',
        source_id: preFill?.source_id || '',
        consult_fee: preFill?.consult_fee || 15000,
        consult_fee_waived: preFill?.consult_fee_waived || false
      });
      setLinkedContact(null);
      setShowSuccess(false);
      setCreatedIntake(null);
      setEmailSentTo(null);
      setEmailFailure(null);
      setError(null);
    }
  }, [isOpen, preFill]);

  const fetchMetadata = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [sourcesRes, paRes, intakeSettingsRes] = await Promise.all([
        axios.get(`${API_URL}/intake/sources`, { headers }),
        axios.get(`${API_URL}/intake/practice-areas`, { headers }),
        axios.get(`${API_URL}/firm-settings/intake_settings`, { headers }).catch(() => ({ data: null }))
      ]);

      setSources(sourcesRes.data || []);
      setPracticeAreas(paRes.data || []);

      // Set default consultation fee from firm settings
      if (intakeSettingsRes.data?.value?.default_consult_fee) {
        setFormData(prev => ({
          ...prev,
          consult_fee: intakeSettingsRes.data.value.default_consult_fee
        }));
      }
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Step 1: Create the intake
      const response = await axios.post(`${API_URL}/intake/quick`, {
        ...formData,
        contact_id: linkedContact?.contact_id || null,
        source_id: formData.source_id ? parseInt(formData.source_id) : null,
        consult_fee: parseInt(formData.consult_fee)
      }, { headers });

      const intake = response.data;
      setCreatedIntake(intake);
      setShowSuccess(true);

      // Step 2: If email provided, automatically send the form link
      const emailAddr = formData.email?.trim();
      if (emailAddr) {
        try {
          const sendRes = await axios.post(`${API_URL}/intake/${intake.id}/send-form`, {
            email: emailAddr,
            consult_fee: parseInt(formData.consult_fee),
            waive_fee: formData.consult_fee_waived
          }, { headers });

          if (sendRes.data?.email_sent) {
            setEmailSentTo(sendRes.data.email_sent_to);
          } else {
            // Fail-loud (A2): the backend now reports send failures instead of
            // pretending. Surface it so staff copy the link by hand.
            setEmailFailure(sendRes.data?.email_error || 'email failed to send');
          }
        } catch (sendErr) {
          console.error('Failed to auto-send intake form:', sendErr);
          setEmailFailure(sendErr.response?.data?.detail || 'email failed to send');
          // Don't block success — intake was created, just couldn't email
        }
      }

      if (onSuccess) {
        onSuccess(intake);
      }
    } catch (err) {
      console.error('Error creating quick intake:', err);
      setError(err.response?.data?.detail || 'Failed to create intake. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (createdIntake?.public_form_url) {
      navigator.clipboard.writeText(createdIntake.public_form_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-virgo-navy-700 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-virgo-navy-600">
          <h2 className="text-xl font-semibold text-white">
            {showSuccess ? 'Intake Created' : 'Quick Intake'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-virgo-navy-600 rounded"
          >
            <X className="w-5 h-5 text-virgo-teal-400" />
          </button>
        </div>

        {/* Content */}
        {showSuccess ? (
          <div className="p-6">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                Intake Created Successfully!
              </h3>
              <p className="text-virgo-teal-300">
                Intake ID: <span className="font-mono">{createdIntake?.id}</span>
              </p>
            </div>

            {/* Email sent confirmation */}
            {emailSentTo && (
              <div className="bg-green-900/30 border border-green-600/50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-green-400 mb-1">
                  <Send className="w-5 h-5" />
                  <span className="font-semibold">Intake Form Sent</span>
                </div>
                <p className="text-green-300 text-sm">
                  Client intake form has been emailed to <strong>{emailSentTo}</strong>
                </p>
              </div>
            )}

            {emailFailure && (
              <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-red-400 mb-1">
                  <Send className="w-5 h-5" />
                  <span className="font-semibold">Email Failed to Send</span>
                </div>
                <p className="text-red-300 text-sm">
                  The form link was created but could not be emailed ({emailFailure}).
                  Copy the link below and send it to the client yourself.
                </p>
              </div>
            )}

            {/* Public Form Link — always show as fallback/reference */}
            <div className="bg-virgo-navy-800 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-virgo-teal-400 mb-2">
                <Link2 className="w-5 h-5" />
                <span className="font-semibold">Client Intake Form Link</span>
              </div>
              <p className="text-sm text-virgo-teal-300 mb-3">
                {emailSentTo
                  ? 'You can also copy this link to share manually:'
                  : 'Send this link to the prospective client to complete their intake form:'}
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={createdIntake?.public_form_url || ''}
                  readOnly
                  className="flex-1 px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white text-sm font-mono"
                />
                <button
                  onClick={copyToClipboard}
                  className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition-colors flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Fee Info */}
            <div className="bg-virgo-navy-800 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-amber-400 mb-2">
                <DollarSign className="w-5 h-5" />
                <span className="font-semibold">Consultation Fee</span>
              </div>
              {createdIntake?.consult_fee_waived ? (
                <p className="text-virgo-teal-300">Fee waived for this client</p>
              ) : (
                <p className="text-virgo-teal-300">
                  ${(createdIntake?.consult_fee / 100).toFixed(2)} - Will be invoiced after client submits form
                </p>
              )}
            </div>

            <button
              onClick={onClose}
              className="w-full py-3 bg-virgo-teal-600 text-white font-semibold rounded-lg hover:bg-virgo-teal-700 transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4 mb-6">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            <p className="text-virgo-teal-300 text-sm mb-6">
              Create a quick intake with minimal information. If an email is provided, the intake form link will be sent automatically.
            </p>

            <div className="mb-4">
              {linkedContact ? (
                <div className="flex items-center justify-between rounded-lg border border-virgo-gold-500/40 bg-virgo-gold-500/10 px-3 py-2">
                  <span className="text-sm text-virgo-gold-200">Returning client: <strong>{linkedContact.name}</strong></span>
                  <button type="button" onClick={() => setLinkedContact(null)}
                    className="text-xs text-virgo-teal-400 hover:text-white underline">unlink</button>
                </div>
              ) : (
                <>
                  <label className="block text-sm text-virgo-teal-400 mb-1">Returning client? Search to prefill and link</label>
                  <ContactPicker
                    onSelect={(c) => {
                      setLinkedContact(c);
                      setFormData((f) => ({
                        ...f,
                        first_name: c.first_name || f.first_name,
                        last_name: c.last_name || f.last_name,
                        email: c.email || f.email,
                        primary_phone: c.phone || f.primary_phone,
                      }));
                    }}
                  />
                </>
              )}
            </div>

            {/* Name */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email
                </label>
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Form link sent automatically"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent placeholder-virgo-teal-600"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone
                </label>
                <input
                  type="tel"
                  name="primary_phone"
                  value={formData.primary_phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Practice Area & Source */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">
                  <Briefcase className="w-4 h-4 inline mr-1" />
                  Practice Area
                </label>
                <select
                  name="practice_area"
                  value={formData.practice_area}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  {practiceAreas.map((pa, i) => (
                    <option key={i} value={pa}>{pa}</option>
                  ))}
                  <option value="Family Law">Family Law</option>
                  <option value="Criminal Defense">Criminal Defense</option>
                  <option value="Personal Injury">Personal Injury</option>
                  <option value="Business Law">Business Law</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Estate Planning">Estate Planning</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">
                  Source
                </label>
                <select
                  name="source_id"
                  value={formData.source_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  {sources.map(source => (
                    <option key={source.id} value={source.id}>{source.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Consultation Fee */}
            <div className="mb-6 p-4 bg-virgo-navy-800 rounded-lg">
              <div className="flex items-center gap-2 text-amber-400 mb-3">
                <DollarSign className="w-5 h-5" />
                <span className="font-semibold">Consultation Fee</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-virgo-teal-400 mb-1">
                    Amount (in dollars)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-virgo-teal-400">$</span>
                    <input
                      type="number"
                      name="consult_fee"
                      value={formData.consult_fee / 100}
                      onChange={(e) => setFormData(prev => ({ ...prev, consult_fee: parseFloat(e.target.value) * 100 || 0 }))}
                      step="0.01"
                      min="0"
                      className="w-full pl-8 pr-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="consult_fee_waived"
                      checked={formData.consult_fee_waived}
                      onChange={handleChange}
                      className="w-4 h-4 text-virgo-teal-600 bg-virgo-navy-600 border-virgo-navy-500 rounded focus:ring-virgo-teal-500"
                    />
                    <span className="text-virgo-teal-300">Waive fee</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-virgo-teal-600 text-white font-semibold rounded-lg hover:bg-virgo-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Intake & Generate Link'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default QuickIntakeModal;
